import { ElementRef } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { IBreadcrumbRoute } from '@model/breadcrumb-route.model';
import {
  difference,
  isArray,
  isEmpty as isEmptyLodash,
  isEqual,
  isObject,
  transform,
} from 'lodash';
import * as moment from 'moment';
import { HttpContextToken } from '@angular/common/http';

export const COMPONENT_ERROR_HANDLE = new HttpContextToken(() => false);
export const COMPONENT_LOADER_HANDLE = new HttpContextToken(() => false);

export interface IJSON {
  [k: string]: any;
}

export function basename(path: string) {
  return path.replace(/.*\//, '');
}

export const isEmpty = (val: any) =>
  val == null || !(Object.keys(val) || val).length;
export const poiLabel = (line: string, lineName: string, branch: string) =>
  [line, lineName, branch].join('/');

export function queryParamsConstructor(
  queryParams: string,
  params: any,
  parmasName: string
) {
  if (params != null) {
    queryParams === ''
      ? (queryParams += '?' + parmasName + '=' + params)
      : (queryParams += '&' + parmasName + '=' + params);
  }
  return queryParams;
}

/**
 * Arrays intersection
 * @param arr1
 * @param arr2
 * @returns intersection beetween two arrays in input
 */
export function arrayIntersection(arr1: any[], arr2: any[]): any[] {
  return arr1.filter((x) => arr2.includes(x));
}

/**
 * Arrays difference
 * @param arr1
 * @param arr2
 * @returns difference beetween two arrays in input
 */
export function arrayDifference(arr1: any[], arr2: any[]): any[] {
  return arr1
    .filter((x) => !arr2.includes(x))
    .concat(arr2.filter((x) => !arr1.includes(x)));
}

/**
 * Objects diff
 * @param object
 * @param base
 * @returns  the difference beetween object and base
 */
export function objectDiff(object: object, base: object): object {
  const changes = (object: object, base: object): object => {
    return transform(object, (result, value, key) => {
      if (!isEqual(value, base[key])) {
        if (isArray(value)) {
          (result as any)[key] = difference(value, base[key]);
        } else if (isObject(value) && isObject(base[key])) {
          (result as any)[key] = changes(value, base[key]);
        } else {
          (result as any)[key] = value;
        }
      }
    });
  };
  return changes(object, base);
}

/**
 * Objects equal
 * @param obj1
 * @param obj2
 * @returns true if obj1 equal obj2; false otherwise
 */
export function objectEqual(obj1: any, obj2: any): boolean {
  return isEqual(obj1, obj2);
}

/**
 * Formats bytes
 * @param bytes
 * @param [decimals]
 * @returns File size in term of 'Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function randomID() {
  return Math.random().toString(36).slice(2);
}

export function validatorPasswordLenght(formControl: AbstractControl) {
  let REGEX_LENGHT = /^.{8,30}$/; // Regular Expression 1: ^.{8,30}$

  return REGEX_LENGHT.test(formControl.value) ? null : { lenght: true };
}

export function validatorPasswordCaps(formControl: AbstractControl) {
  let REGEX_CAPS = /.*[A-Z].*/; // Regular Expression 2: ^(?=.*[A-Z])[A-Z].*$

  return REGEX_CAPS.test(formControl.value) ? null : { caps: true };
}

export function validatorPasswordSpecialChart(formControl: AbstractControl) {
  let REGEX_SPECIAL_CHART = /^(?=.*[@$!%*?&^]).*$/; // Regular Expression 3: ^(?=.*[@$!%*?&^]).*$

  return REGEX_SPECIAL_CHART.test(formControl.value)
    ? null
    : { specialChart: true };
}

export function isEmptyObject(obj: any): boolean {
  return isEmptyLodash(obj);
}

export function focusFirstElement(firstElement: ElementRef) {
  if (!firstElement) {
    throw new Error("h1 non presente all'interno della pagina");
  } else {
    firstElement.nativeElement.tabIndex = -1;
    firstElement.nativeElement.focus();
    firstElement.nativeElement.dataset['focusMouse'] = 'true';
    firstElement.nativeElement.classList.remove('focus--mouse');
  }
}

export function addMainTheme() {
  const main = document.querySelector('main');
  if (main) {
    main.classList.add('theme-primary');
  }
}

export function removeMainTheme() {
  const main = document.querySelector('main');
  if (main) {
    main.classList.remove('theme-primary');
  }
}

/**
 * Check if an array of object is equal to another one
 * @param value
 * @param other
 */
export function isEqualArrayOfObjects(value: any[], other: any[]) {
  // Get the value type
  let type = Object.prototype.toString.call(value);

  // If the two objects are not the same type, return false
  if (type !== Object.prototype.toString.call(other)) return false;

  // If items are not an object or array, return false
  if (['[object Array]', '[object Object]'].indexOf(type) < 0) return false;

  // Compare the length of the length of the two items
  let valueLen =
    type === '[object Array]' ? value.length : Object.keys(value).length;
  let otherLen =
    type === '[object Array]' ? other.length : Object.keys(other).length;
  if (valueLen !== otherLen) return false;

  // Compare two items
  let compare = (item1: any, item2: any) => {
    // Get the object type
    var itemType = Object.prototype.toString.call(item1);

    // If an object or array, compare recursively
    if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
      if (!isEqualArrayOfObjects(item1, item2)) return false;
    }
    // Otherwise, do a simple comparison
    else {
      // If the two items are not the same type, return false
      if (itemType !== Object.prototype.toString.call(item2)) return false;

      // Else if it's a function, convert to a string and compare
      // Otherwise, just compare
      if (itemType === '[object Function]') {
        if (item1.toString() !== item2.toString()) return false;
      } else {
        if (item1 !== item2) return false;
      }
    }
    return true;
  };

  // Compare properties
  if (type === '[object Array]') {
    for (let i = 0; i < valueLen; i++) {
      if (compare(value[i], other[i]) === false) return false;
    }
  } else {
    for (let key in value) {
      if (value.hasOwnProperty(key)) {
        if (compare(value[key], other[key]) === false) return false;
      }
    }
  }

  // If nothing failed, return true
  return true;
}

export function convertDateToDisplayFormat(date: string) {
  return moment(date, 'YYYY-MM-DD', true).format('DD/MM/YYYY');
}

export function convertDateFromDisplayFormat(date: string) {
  return moment(date, 'DD/MM/YYYY', true).format('YYYY-MM-DD');
}

export function blobToFile(
  theBlob: Blob | undefined,
  fileName: string | undefined
): File | undefined {
  if (theBlob) {
    let b: any = theBlob;
    //A Blob() is almost a File() - it's just missing the two properties below which we will add
    b.lastModifiedDate = new Date();
    b.name = fileName;

    //Cast to a File() type
    return <File>theBlob;
  }
  return undefined;
}

export async function fileToBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result?.toString()?.split(',')?.[1]);
    reader.onerror = (error) => reject(error);
  });
}

export function compareDates(date1: string, date2: string, format: string) {
  return moment(date1, format).isBefore(moment(date2, format));
}

export function areEqualDates(date1: string, date2: string, format: string) {
  return moment(date1, format).isSame(moment(date2, format));
}

// URL
export function composeURL(...slugs: string[]) {
  return slugs.join('/');
}

export function replaceURLParam(url: string, value: string) {
  return url.replace(/:.+?(?=\/)|:.+$/, value);
}

// Breadcrumbs
export function usePageBreadcrumb(breadcrumbs: IBreadcrumbRoute[]) {
  return breadcrumbs.map((b, i) =>
    i === breadcrumbs.length - 1
      ? {
          ...b,
          url: null,
        }
      : b
  );
}

export function getHHmmHoursFromNumber(hours: number | undefined): string {
  if (hours !== undefined && hours > 0) {
    const durata: moment.Duration = moment.duration(hours, 'hours');
    const ore: number = Math.floor(durata.asHours());
    const minuti: number = Math.floor((durata.asHours() - ore) * 60);
    return `${ore < 10 ? '0' : ''}${ore}:${minuti < 10 ? '0' : ''}${minuti}`;
  } else {
    return '0';
  }
}

export function onDateBlur({
  minDate,
  maxDate,
}: {
  minDate?: () => Date;
  maxDate?: () => Date;
}): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const dateValue = moment(group.value, 'YYYY-MM-DD');
    const localMinDate = moment(minDate?.(), 'YYYY-MM-DD').startOf('day');
    const localMaxDate = moment(maxDate?.(), 'YYYY-MM-DD').startOf('day');

    if (dateValue.isValid()) {
      if (
        (localMinDate.isValid() && dateValue.isBefore(localMinDate)) ||
        (localMaxDate.isValid() && dateValue.isAfter(localMaxDate))
      ) {
        return {
          invalidDate: 'GENERIC_INVALID_DATE',
        };
      }
      return null;
    } else {
      return null;
    }
  };
}

export function calcolateVotoFinale(
  votoTeoria: number,
  votoPratica: number
): number | null {
  return Math.round((votoTeoria + votoPratica) / 2) || null;
}

export function formatMinuteHours(minute: number): string {
  return moment()
    .startOf('day')
    .add(minute, 'minutes')
    .format('HH:mm')
    .toString();
}

export function createLinkForDownload(blob: Blob, fileName: string = '') {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
}


export function toTitleCase(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}